import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { authService, User } from '../../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAnyPermission?: boolean; // If true, user needs ANY of the permissions, otherwise ALL
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredPermissions = [],
  requireAnyPermission = false,
}) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Combine single permission with permissions array
  const allRequiredPermissions = requiredPermission 
    ? [requiredPermission, ...requiredPermissions]
    : requiredPermissions;

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!authService.isAuthenticated()) {
          setLoading(false);
          return;
        }

        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (err) {
        console.error('Auth check failed:', err);
        setError('Failed to verify authentication');
        authService.clearTokens();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Verificando autenticación...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <Typography variant="h6" color="error">
          Error de autenticación
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!authService.isAuthenticated() || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (allRequiredPermissions.length > 0) {
    const hasPermission = requireAnyPermission
      ? authService.hasAnyPermission(allRequiredPermissions, user)
      : allRequiredPermissions.every(permission => 
          authService.hasPermission(permission, user)
        );

    if (!hasPermission) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
        >
          <Typography variant="h6" color="error">
            Acceso Denegado
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            No tienes permisos para acceder a esta página.
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;